import { matchesModelFilters } from '@context/ProjectFilters/matchesModelFilters';
import useChangeFilterHandler from '@hooks/useChangeFilterHandler';
import {
  useModelFilterActions,
  useModelFilters,
  withScopeType,
} from '@store/zustand/model-filters';
import { ScopeType } from '@utils/domains';

/**
 * The Scope type switch behind the drawer guard every model-filter write
 * carries.
 *
 * The switch keeps the Scope selection exactly as it is — the type changes
 * what the selected ids mean, never which ids are selected — so a value that
 * matches nothing under the new type stays ticked and matches again on the
 * way back. The open drawer's entity, or the selected edge's endpoints, is
 * judged against the new type with that selection, and the drawer closes
 * before the canvas hides it. Only then is the type written, together with
 * the unchanged ids, in one store write. Declining the unsaved-changes
 * prompt drops the switch; a switch to the current type writes nothing.
 */
export const useSetScopeType = () => {
  const changeFilterHandler = useChangeFilterHandler();
  const state = useModelFilters();
  const { setScopeType } = useModelFilterActions();

  return (scopeType: ScopeType): Promise<void> => {
    if (scopeType === state.scopeType) return Promise.resolve();

    const { domains } = state;
    const next = withScopeType(state, scopeType, domains);

    return changeFilterHandler({
      isNodeVisible: (_values, node) =>
        !node || matchesModelFilters(node, next),
      onSetValues: () => setScopeType(scopeType, domains),
    })(domains).catch(() => undefined);
  };
};
